import { ApolloCache, gql } from '@apollo/client';
import { achievementFragment } from '../fragments/achievement';
import { categoryFragment } from '../fragments/category';
import { AchievementFieldsFragment, UpdateAchievementMutation } from '../generated/graphql';

export const updateAchievementMutation = gql`
    ${achievementFragment}
    ${categoryFragment}

    mutation UpdateAchievement(
        $achievementId: String!
        $name: String!
        $description: String!
        $categoryId: String!
        $rarity: AchievementRarity!
        $icon: String!
        $achievementConditions: [AchievementConditionCreateInput!]
    ) {
        updateAchievement(
            data: {
                id: $achievementId
                name: $name
                description: $description
                categoryId: $categoryId
                rarity: $rarity
                icon: $icon
                achievementConditions: $achievementConditions
            }
        ) {
            ...AchievementFields
            category {
                ...CategoryFields
            }
        }
    }
`;

export function updateAchievementCache(
    cache: ApolloCache<any>,
    { data }: { data?: UpdateAchievementMutation | null },
    oldCategoryId?: string,
) {
    if (!data?.updateAchievement) return;

    const updatedAchievement = data.updateAchievement;

    // If category changed, manually update the OLD category
    // The NEW category is returned in mutation response and Apollo updates it automatically
    if (oldCategoryId && updatedAchievement.categoryId !== oldCategoryId) {
        // Read old achievement to check if it was achieved (need this to update completed count)
        const achievementId = updatedAchievement.id;
        const achievementCacheKey = `Achievement:${achievementId}`;
        const oldAchievement = cache.readFragment<AchievementFieldsFragment>({
            id: achievementCacheKey,
            fragment: achievementFragment,
            fragmentName: 'AchievementFields',
        });

        const isAchieved = !!oldAchievement?.achievedOn;

        cache.modify({
            id: cache.identify({ __typename: 'Category', id: oldCategoryId }),
            fields: {
                achievementProgressTotal(existingTotal = 0) {
                    return Math.max(0, existingTotal - 1);
                },
                achievementProgressCompleted(existingCompleted = 0) {
                    return isAchieved ? Math.max(0, existingCompleted - 1) : existingCompleted;
                },
            },
        });
    }
}
